import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { backendConfig } from '../config/backendConfig';
import { motion } from 'framer-motion';
import KidBackground from '../ui/backgrounds/KidBackground';
import BillingScenarios from '../components/BillingScenarios';
import AddMentorCheckout from '../components/AddMentorCheckout';
import OrganizationAccountSettingsModal from '../components/OrganizationAccountSettingsModal';
import { User } from 'lucide-react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { authFetch } from '../utils/authFetch';

const orgDashboardSnapshotKey = (uid) => `organization:dashboard:${uid || 'anon'}`;

export default function OrganizationDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddMentor, setShowAddMentor] = useState(false);
  const [showAddMentorCount, setShowAddMentorCount] = useState(false);
  const [newMentorEmail, setNewMentorEmail] = useState('');
  const [newMentorName, setNewMentorName] = useState('');
  const [mentorCount, setMentorCount] = useState(1);
  const [mentorProgress, setMentorProgress] = useState({});
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [showBillingScenarios, setShowBillingScenarios] = useState(false);
  const [showAddMentorCheckout, setShowAddMentorCheckout] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;
    try {
      const raw = sessionStorage.getItem(orgDashboardSnapshotKey(user.uid));
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed?.mentors)) setMentors(parsed.mentors);
      if (parsed?.mentorProgress && typeof parsed.mentorProgress === 'object') setMentorProgress(parsed.mentorProgress);
      if (parsed?.membershipStatus && typeof parsed.membershipStatus === 'object') setMembershipStatus(parsed.membershipStatus);
    } catch (_) {}
  }, [user?.uid]);

  const fetchOrganizationData = useCallback(async () => {
    // Prevent multiple concurrent requests
    if (isFetching) {
      console.log('OrganizationDashboard: Request already in progress, skipping...');
      return;
    }
    
    try {
      setIsFetching(true);
      setLoading(true);
      console.log('OrganizationDashboard: Starting to fetch organization data for user:', user.uid);
      
      // Mentors will be fetched from the dashboard API below

      // Fetch organization dashboard data (includes membership status)
      console.log('OrganizationDashboard: Fetching organization dashboard data...');
      const dashboardController = new AbortController();
      const dashboardTimeout = setTimeout(() => dashboardController.abort(), 30000); // Increased to 30 seconds
      
      try {
        console.log('OrganizationDashboard: Fetching dashboard data for user.uid:', user.uid);
        const dashboardResponse = await fetch(`${backendConfig.baseUrl}/api/organization/dashboard?ownerId=${user.uid}`, {
          signal: dashboardController.signal
        });
        clearTimeout(dashboardTimeout);
        console.log('OrganizationDashboard: Dashboard response status:', dashboardResponse.status);
        console.log('OrganizationDashboard: Dashboard response URL:', `${backendConfig.baseUrl}/api/organization/dashboard?ownerId=${user.uid}`);
        if (dashboardResponse.ok) {
          const dashboardData = await dashboardResponse.json();
          console.log('OrganizationDashboard: Dashboard data:', dashboardData);
          if (dashboardData.ok && dashboardData.dashboard) {
            setMembershipStatus(dashboardData.dashboard.membership || {});
            // Only update mentors if we got valid data
            if (Array.isArray(dashboardData.dashboard.mentors)) {
              setMentors(dashboardData.dashboard.mentors);
            }
            setMentorProgress(dashboardData.dashboard.mentorProgress || {});
          } else {
            console.error('OrganizationDashboard: Invalid dashboard data structure:', dashboardData);
            // Don't clear existing mentors if API returns invalid data
          }
        } else {
          console.error('OrganizationDashboard: Dashboard fetch failed:', dashboardResponse.status);
          // Don't clear existing mentors on API failure
        }
      } catch (dashboardError) {
        clearTimeout(dashboardTimeout);
        console.error('OrganizationDashboard: Dashboard fetch error:', dashboardError);
      }

      // Mentor progress is fetched from the dashboard API above

    } catch (error) {
      console.error('OrganizationDashboard: Error fetching organization data:', error);
      setError('Failed to load organization data');
    } finally {
      setIsFetching(false);
      console.log('OrganizationDashboard: Finished fetching data, setting loading to false');
      setLoading(false);
    }
    
    // Ensure loading is set to false even if there are issues
    setTimeout(() => {
      if (loading) {
        console.log('OrganizationDashboard: Force setting loading to false after timeout');
        setIsFetching(false);
        setLoading(false);
      }
    }, 15000); // 15 second fallback
  }, [user.uid]);

  useEffect(() => {
    fetchOrganizationData();
  }, [fetchOrganizationData]);

  useEffect(() => {
    if (!user?.uid) return;
    try {
      sessionStorage.setItem(
        orgDashboardSnapshotKey(user.uid),
        JSON.stringify({ mentors, mentorProgress, membershipStatus })
      );
    } catch (_) {}
  }, [user?.uid, mentors, mentorProgress, membershipStatus]);

  // Check membership status and redirect expired users
  // Only redirect if membership status is explicitly set and not active
  // Don't redirect if membershipStatus is null (still loading) or if it's an empty object
  useEffect(() => {
    if (membershipStatus && Object.keys(membershipStatus).length > 0 && !membershipStatus.isActive) {
      // Only redirect if we're sure the membership is expired (not just loading)
      // Check if we have explicit status information
      if (membershipStatus.status === 'expired' || membershipStatus.status === 'canceled' || 
          (membershipStatus.expiresAt && new Date(membershipStatus.expiresAt) < new Date())) {
        console.log('OrganizationDashboard: Membership is expired, redirecting to renewal...');
        navigate('/organization-renewal');
      }
    }
  }, [membershipStatus, navigate]);

  // Listen for subscription updates from the navbar
  useEffect(() => {
    const handleSubscriptionUpdate = () => {
      console.log('OrganizationDashboard: Subscription updated, refreshing data...');
      fetchOrganizationData();
    };

    window.addEventListener('subscriptionUpdated', handleSubscriptionUpdate);
    
    return () => {
      window.removeEventListener('subscriptionUpdated', handleSubscriptionUpdate);
    };
  }, [fetchOrganizationData]);

  const addMentor = async (e) => {
    e.preventDefault();
    if (!newMentorEmail || !newMentorName) {
      setError('Please fill in all fields');
      return;
    }

    try {
      const response = await fetch(`${backendConfig.baseUrl}/api/mentors/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ownerId: user.uid,
          mentorEmail: newMentorEmail,
          mentorName: newMentorName
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.ok) {
          setNewMentorEmail('');
          setNewMentorName('');
          setShowAddMentor(false);
          fetchOrganizationData(); // Refresh the list
        } else {
          setError(data.error || 'Failed to add mentor');
        }
      } else {
        setError('Failed to add mentor');
      }
    } catch (error) {
      console.error('Error adding mentor:', error);
      setError('Failed to add mentor');
    }
  };

  const removeMentor = async (mentorId) => {
    if (!window.confirm('Are you sure you want to remove this mentor?')) {
      return;
    }

    try {
      const response = await fetch(`${backendConfig.baseUrl}/api/mentors/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mentorId })
      });

      if (response.ok) {
        fetchOrganizationData(); // Refresh the list
      } else {
        setError('Failed to remove mentor');
      }
    } catch (error) {
      console.error('Error removing mentor:', error);
      setError('Failed to remove mentor');
    }
  };

  const handleAddMentorClick = () => {
    // Always show mentor count selection first, regardless of membership status
    setShowAddMentorCount(true);
  };

  const handleMentorCountSubmit = () => {
    // Close the modal first
    setShowAddMentorCount(false);
    
    if (membershipStatus?.isActive) {
      // For active memberships, show billing scenarios
      // Calculate current monthly price based on plan and seat count
      const currentSeatCount = mentors.length;
      const planPricing = {
        'mentor-basic': { 1: 999, 2: 899, 3: 799, 4: 749, 5: 699, 6: 649, 7: 599, 8: 549, 9: 499, 10: 449 },
        'mentor-pro': { 1: 1299, 2: 1199, 3: 1099, 4: 1049, 5: 999, 6: 949, 7: 899, 8: 849, 9: 799, 10: 749 },
        'mentor-premium': { 1: 1599, 2: 1499, 3: 1399, 4: 1349, 5: 1299, 6: 1249, 7: 1199, 8: 1149, 9: 1099, 10: 1049 }
      };
      
      const planPrices = planPricing[membershipStatus.plan] || planPricing['mentor-basic'];
      const currentPricePerSeat = planPrices[Math.min(currentSeatCount, 10)] || planPrices[10];
      const currentMonthlyTotal = currentPricePerSeat * currentSeatCount;
      
      setCurrentPlan({
        planType: membershipStatus.plan,
        totalSeats: mentors.length,
        usedSeats: mentors.length,
        daysRemaining: membershipStatus.daysRemaining || 15,
        totalMonthlyPrice: currentMonthlyTotal
      });
      setShowBillingScenarios(true);
    } else {
      // For expired memberships, go directly to membership page
      setTimeout(() => {
        navigate('/membership', { 
          state: { 
            isOrganizationRenewal: true,
            mentorCount: mentorCount,
            userData: { uid: user.uid, email: user.email }
          } 
        });
      }, 100); // Small delay to ensure modal closes first
    }
  };

  const handleBillingScenarioSelect = async (scenario) => {
    setShowBillingScenarios(false);
    setShowAddMentorCount(false);
    
    try {
      if (scenario.type === 'add_seats') {
        // For adding seats to existing membership, show professional checkout
        setShowAddMentorCheckout(true);
      } else if (scenario.type === 'upgrade_plan') {
        // For plan upgrades, call the upgrade API directly
        const response = await authFetch(`${backendConfig.baseUrl}/api/membership/upgrade-plan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            uid: user.uid,
            newPlanType: scenario.planType,
            seatCount: mentors.length + mentorCount
          })
        });

        const result = await response.json();
        
        if (result.ok) {
          // Show success message
          alert(`✅ ${result.billing.confirmation}`);
          
          // Refresh organization data
          await fetchOrganizationData();
        } else {
          alert('Error upgrading plan: ' + result.error);
        }
      } else {
        // For other scenarios, navigate to membership page
        setTimeout(() => {
          navigate('/membership', {
            state: {
              isOrganizationRenewal: false,
              mentorCount: mentors.length + mentorCount,
              billingScenario: scenario,
              userData: { uid: user.uid, email: user.email }
            }
          });
        }, 100);
      }
    } catch (error) {
      console.error('Error processing billing scenario:', error);
      alert('Error processing request. Please try again.');
    }
  };

  const handleAddMentorConfirm = async (checkoutData) => {
    setShowAddMentorCheckout(false);
    
    try {
      const response = await authFetch(`${backendConfig.baseUrl}/api/membership/add-mentors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: user.uid,
          additionalSeats: mentorCount,
          planType: membershipStatus.plan
        })
      });

      const result = await response.json();
      
      if (result.ok) {
        // Show success message
        alert(`✅ ${result.billing.confirmation}`);
        
        // Redirect to mentor setup process for the new mentors
        setTimeout(() => {
          navigate('/mentor-setup', {
            state: {
              isAddMentorFlow: true,
              mentorCount: mentorCount,
              userData: { uid: user.uid, email: user.email },
              organizationName: membershipStatus?.organizationName || 'Organization'
            }
          });
        }, 1500);
      } else {
        alert('Error adding mentors: ' + result.error);
      }
    } catch (error) {
      console.error('Error adding mentors:', error);
      alert('Error adding mentors. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-yellow-100 relative">
      <KidBackground variant="aurora" intensity={0.6} />
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative z-10"
      >
        {loading && (
          <div className="mx-auto max-w-7xl px-6 pt-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-800">
              Refreshing organization dashboard...
            </div>
          </div>
        )}
        {/* Header */}
        <header className="sticky top-0 z-20 bg-white/95 backdrop-blur-md border-b border-gray-200/60 shadow-sm">
          <div className="mx-auto max-w-7xl px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white text-xl font-bold">A</span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>Organization Dashboard</h1>
                  <p className="text-sm text-gray-600">Manage your mentors and track progress</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <nav className="flex items-center gap-1 bg-gray-50 rounded-xl p-1">
                  <button 
                    onClick={() => navigate('/')} 
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-all duration-200 text-sm font-medium"
                  >
                    Artist Corner
                  </button>
                  <button 
                    onClick={() => navigate('/artist-wall')} 
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-all duration-200 text-sm font-medium"
                  >
                    Collections
                  </button>
                </nav>
                {/* Account Button */}
                <button
                  onClick={() => setShowAccountSettings(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all duration-200"
                >
                  <User className="w-4 h-4" />
                  <span className="text-sm font-medium">Account</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Co-Learning Space Button - Top Center */}
        <section className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 border-b border-amber-400/60">
          <div className="mx-auto max-w-7xl px-6 py-8">
            <div className="flex items-center justify-center">
              <div className="inline-flex items-center gap-6 bg-white/95 backdrop-blur-sm rounded-3xl px-8 py-6 shadow-2xl border border-amber-200/60">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center shadow-xl">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-1">Co-Learning Space</h2>
                  <p className="text-sm text-gray-600">Access shared artworks from your organization</p>
                </div>
                <button 
                  onClick={async () => {
                    if (!user) return;
                    let ownerId = user.uid;
                    try {
                      const userDocRef = doc(db, 'users', user.uid);
                      const userDoc = await getDoc(userDocRef);
                      if (userDoc.exists()) {
                        const userData = userDoc.data();
                        ownerId = userData.parentId || userData.primaryId || userData.orgId || user.uid;
                        if (userData.mentorType === 'organization' && !userData.isPrimaryId && userData.primaryId) {
                          ownerId = userData.primaryId;
                        } else if (userData.isPrimaryId) {
                          ownerId = user.uid;
                        }
                      }
                    } catch (error) {
                      console.error('Error fetching user document:', error);
                    }
                    navigate(`/mentors/co-learning?scope=org&ownerId=${encodeURIComponent(ownerId)}`);
                  }}
                  className="ml-6 flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl font-semibold"
                >
                  Access Co-Learning Space
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-6 py-10">
          {/* Membership Status Card */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 mb-8 border border-white/30">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Membership Status</h2>
                {user?.email && (
                  <p className="text-sm text-gray-600">Logged in as: {user.email}</p>
                )}
              </div>
              <div className="flex items-center gap-4">
                <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                  membershipStatus?.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : membershipStatus && Object.keys(membershipStatus).length > 0
                    ? 'bg-red-100 text-red-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {membershipStatus?.isActive ? 'Active' : 
                   membershipStatus && Object.keys(membershipStatus).length > 0 ? 'Expired' : 'Loading...'}
                </div>
                {membershipStatus?.plan && (
                  <div className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
                    Plan: {membershipStatus.plan}
                  </div>
                )}
                {membershipStatus?.expiresAt && (
                  <div className="text-sm text-gray-600">
                    {membershipStatus.isActive ? 'Expires' : 'Expired'}: {new Date(membershipStatus.expiresAt).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Cancellation Warning */}
        {membershipStatus?.canceled && membershipStatus?.isActive && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-orange-800">
                  Your account will be deactivated: {new Date(membershipStatus.expiresAt).toLocaleDateString()}
                </h3>
                <p className="mt-1 text-sm text-orange-700">
                  Please resume subscription through <strong>Manage Subscription</strong> in Account Settings
                </p>
              </div>
            </div>
          </div>
        )}

          {/* Mentors Section */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 mb-8 border border-white/30">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Mentors</h2>
              <button
                onClick={handleAddMentorClick}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg font-semibold"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Mentor
              </button>
            </div>

            {mentors.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <svg className="mx-auto h-16 w-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                <p className="text-lg font-medium text-gray-600">No mentors added yet</p>
                <p className="text-sm text-gray-500 mt-2">Click "Add Mentor" to get started</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {mentors.map((mentor) => (
                  <div key={mentor.id} className="bg-white/60 backdrop-blur-sm border border-gray-200/60 rounded-xl p-5 hover:shadow-lg transition-all duration-200 hover:border-amber-300">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-lg mb-1">{mentor.name}</h3>
                        <p className="text-gray-600 text-sm">{mentor.email}</p>
                      </div>
                      <button
                        onClick={() => removeMentor(mentor.id)}
                        className="ml-4 px-4 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all duration-200 font-medium text-sm border border-red-200 hover:border-red-300"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        </motion.div>

      {/* Billing Scenarios Modal */}
      {showBillingScenarios && currentPlan && (
        <BillingScenarios
          currentPlan={currentPlan}
          requestedSeats={mentorCount}
          onSelectScenario={handleBillingScenarioSelect}
          onCancel={() => setShowBillingScenarios(false)}
        />
      )}

      {showAddMentorCheckout && currentPlan && (
        <AddMentorCheckout
          currentPlan={currentPlan}
          mentorCount={mentorCount}
          onConfirm={handleAddMentorConfirm}
          onCancel={() => setShowAddMentorCheckout(false)}
        />
      )}

      {/* Add Mentor Count Selection Modal */}
      {showAddMentorCount && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Add Mentors</h3>
            <p className="text-gray-600 mb-6">
              {membershipStatus?.isActive 
                ? `How many additional mentors would you like to add? (You currently have ${mentors.length} mentor${mentors.length !== 1 ? 's' : ''})`
                : 'How many mentors would you like to add?'
              }
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Mentors</label>
              <select
                value={mentorCount}
                onChange={(e) => setMentorCount(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: 20 }, (_, i) => i + 1).map((count) => (
                  <option key={count} value={count}>
                    {count} {count === 1 ? 'Mentor' : 'Mentors'}
                  </option>
                ))}
              </select>
            </div>


            <div className="flex gap-3">
              <button
                onClick={() => setShowAddMentorCount(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleMentorCountSubmit}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {membershipStatus?.isActive ? 'Continue to Plans' : 'Renew & Add Mentors'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Add Mentor Modal (Legacy - for single mentor addition) */}
      {showAddMentor && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Add New Mentor</h3>
            <form onSubmit={addMentor}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Mentor Name</label>
                  <input
                    type="text"
                    value={newMentorName}
                    onChange={(e) => setNewMentorName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter mentor name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Mentor Email</label>
                  <input
                    type="email"
                    value={newMentorEmail}
                    onChange={(e) => setNewMentorEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="mentor@school.org"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddMentor(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Mentor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Account Settings Modal */}
      <OrganizationAccountSettingsModal 
        isOpen={showAccountSettings} 
        onClose={() => setShowAccountSettings(false)}
      />
    </div>
  );
}